import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import DataTable from "react-data-table-component";
import axios from "axios";
import {
    Spinner,
    Badge,
    Button,
    Card,
    Row,
    Col
} from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import {
    FiSearch,
    FiRefreshCw,
    FiToggleLeft,
    FiToggleRight,
    FiUsers,
    FiUser,
    FiUserX,
    FiCheckCircle,
    FiXCircle,
    FiX,
    FiAlertTriangle,
    FiClock,
    FiRotateCcw,
    FiUserCheck,
    FiPlay,
    FiCalendar,
    FiShield
} from "react-icons/fi";
import { useNavigate } from 'react-router-dom';
import "react-toastify/dist/ReactToastify.css";
import StatCard from '../Shared/StatCard';

const Access = () => {
    const [customers, setCustomers] = useState([]);
    const [displayData, setDisplayData] = useState([]);
    const [incompleteData, setIncompleteData] = useState({
        missing: [],
        failed: [],
        allIncomplete: []
    });
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeFilter, setActiveFilter] = useState(null);
    const hasFetched = useRef(false);
    const navigate = useNavigate();

    const stats = useMemo(() => {
        const actualCustomers = customers.filter(c =>
            !c.guarantor_for || c.guarantor_for === ""
        );

        const totalCustomers = actualCustomers.length;
        const missingCount = incompleteData.missing?.length || 0;
        const failedCount = incompleteData.failed?.length || 0;
        const totalIncompleteCount = incompleteData.allIncomplete?.length || 0;

        const missingCustomers = incompleteData.missing?.length > 0
            ? customers.filter(c => incompleteData.missing.includes(c.boDDARefNo))
            : [];

        const failedCustomers = incompleteData.failed?.length > 0
            ? customers.filter(c => incompleteData.failed.includes(c.boDDARefNo))
            : [];

        const justSignedUpCustomers = actualCustomers.filter(c =>
            !c.boDDARefNo || c.boDDARefNo === ""
        );
        const justSignedUpCount = justSignedUpCustomers.length;

        const activeCustomersCount = totalCustomers - totalIncompleteCount - justSignedUpCount;

        const activeCustomers = actualCustomers.filter(c =>
            c.boDDARefNo &&
            c.boDDARefNo !== "" &&
            !incompleteData.missing.includes(c.boDDARefNo) &&
            !incompleteData.failed.includes(c.boDDARefNo)
        );

        const cancelTriggered = activeCustomers.filter(c => c.requestType === "Cancellation").length;
        const privilegeEnabled = activeCustomers.filter(c => c.cancelFlag === 1).length;
        const privilegeDisabled = activeCustomers.filter(c => c.cancelFlag === 0).length;

        const guarantors = customers.filter(c =>
            c.guarantor_for && c.guarantor_for !== ""
        );

        return {
            totalCustomers,
            missingCount,
            failedCount,
            totalIncompleteCount,
            justSignedUpCount,
            activeCustomersCount,
            missingCustomers,
            failedCustomers,
            justSignedUpCustomers,
            activeCustomers,
            cancelTriggered,
            privilegeEnabled,
            privilegeDisabled,
            guarantors: guarantors.length,
            guarantorsList: guarantors
        };
    }, [customers, incompleteData]);

    const getBaseData = useCallback(() => {
        if (activeFilter) {
            switch (activeFilter.type) {
                case 'active':
                    return stats.activeCustomers.sort((a, b) =>
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                case 'cancelTriggered':
                    return stats.activeCustomers.filter(customer =>
                        customer.requestType === "Cancellation"
                    ).sort((a, b) =>
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                case 'justSignedUp':
                    return stats.justSignedUpCustomers.sort((a, b) =>
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                case 'missing':
                    return stats.missingCustomers.sort((a, b) =>
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                case 'failed':
                    return stats.failedCustomers.sort((a, b) =>
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                case 'incomplete':
                    return [...stats.missingCustomers, ...stats.failedCustomers].sort((a, b) =>
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                case 'privilegeEnabled':
                    return customers.filter(customer =>
                        !customer.guarantor_for &&
                        customer.boDDARefNo &&
                        customer.boDDARefNo !== "" &&
                        !incompleteData.missing.includes(customer.boDDARefNo) &&
                        !incompleteData.failed.includes(customer.boDDARefNo) &&
                        customer.cancelFlag === 1
                    ).sort((a, b) =>
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                case 'guarantors':
                    return stats.guarantorsList.sort((a, b) =>
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
                default:
                    return stats.activeCustomers.sort((a, b) =>
                        new Date(b.timestamp) - new Date(a.timestamp)
                    );
            }
        } else {
            return stats.activeCustomers.sort((a, b) =>
                new Date(b.timestamp) - new Date(a.timestamp)
            );
        }
    }, [activeFilter, stats, customers, incompleteData]);

    useEffect(() => {
        let data = getBaseData();

        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            data = data.filter(item => {
                const fieldsToSearch = [
                    item.full_name,
                    item.email_address,
                    item.phone_number,
                    item.customer_id,
                    item.id_number,
                    item.staff_name,
                    item.requestType,
                    item.boDDARefNo,
                    item.boName,
                ];

                return fieldsToSearch.some(field => {
                    if (field === null || field === undefined) return false;
                    return String(field).toLowerCase().includes(term);
                });
            });
        }

        setDisplayData(data);
    }, [searchTerm, getBaseData]);

    const handleStatCardClick = (filterType) => {
        if (activeFilter && activeFilter.type === filterType) {
            setActiveFilter(null);
        } else {
            setActiveFilter({ type: filterType });
        }
    };

    const clearFilters = () => {
        setActiveFilter(null);
        setSearchTerm("");
    };

    const fetchIncompleteCustomers = async () => {
        try {
            const response = await axios.get("https://www.azzenterprises.com/api/customers/incomplete-creation", {
                withCredentials: true
            });

            setIncompleteData({
                missing: response.data.notExists || [],
                failed: response.data.failed || [],
                allIncomplete: response.data.allIncomplete || []
            });
        } catch (err) {
            console.error("Error fetching incomplete customers:", err);
        }
    };

    const fetchAllData = async () => {
        try {
            setLoading(true);
            const customersRes = await axios.get("https://www.azzenterprises.com/api/customers", {
                withCredentials: true
            });

            const normalizedData = customersRes.data.map((c) => ({
                ...c,
                cancelFlag: Number(c.cancelFlag),
            }));

            setCustomers(normalizedData);
            await fetchIncompleteCustomers();

        } catch (err) {
            toast.error("Failed to fetch customers");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!hasFetched.current) {
            fetchAllData();
            hasFetched.current = true;
        }
    }, []);

    const handleUpdateCustomer = async (id, payload) => {
        try {
            const updatedPayload = {
                ...payload,
                cancelFlag: Number(payload.cancelFlag),
            };

            await axios.put(
                `https://www.azzenterprises.com/api/customers/update/${id}`,
                updatedPayload,
                { withCredentials: true }
            );

            setCustomers((prev) =>
                prev.map((c) =>
                    c.customer_id === id ? { ...c, ...updatedPayload } : c
                )
            );

            toast.info(`Privilege ${updatedPayload.cancelFlag === 1 ? "Enabled" : "Disabled"} successfully!`);
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Failed to update user";
            toast.error(message);
        }
    };

    const handleResetCustomer = async (customer) => {
        try {
            if (!customer.boDDARefNo) {
                toast.error("No DDA reference number to reset");
                return;
            }

            const response = await axios.put(
                `https://www.azzenterprises.com/api/customers/reset-dda/${customer.customer_id}`,
                {
                    boDDARefNo: customer.boDDARefNo
                },
                { withCredentials: true }
            );

            if (response.data.success) {
                await fetchAllData();
                toast.success("Customer reset successfully! Customer moved to Just Signed Up.");
            }
        } catch (err) {
            const message = err.response?.data?.message || err.message || "Failed to reset customer";
            toast.error(message);
        }
    };

    const handleStartEgiro = (customerId) => {
        window.open(`/sign-in?customerId=${customerId}`, '_blank');
    };

    const formatDateTime = (dateStr) => {
        if (!dateStr) return "-";

        try {
            const date = new Date(dateStr);
            return date.toLocaleString("en-US", {
                year: "numeric",
                month: "short",
                day: "numeric",
                hour: "2-digit",
                minute: "2-digit",
                hour12: true
            });
        } catch (error) {
            return dateStr;
        }
    };

    const columns = [
        {
            name: "#",
            selector: (row, index) => index + 1,
            width: "60px"
        },
        {
            name: "Customer Name",
            selector: (row) => row.full_name,
            sortable: true,
            width: "250px"
        },
        {
            name: "Status",
            selector: (row) => row.requestType,
            sortable: true,
            width: "130px",
            cell: (row) => {
                if (incompleteData.missing.includes(row.boDDARefNo)) {
                    return <Badge bg="warning">Missing</Badge>;
                }
                if (incompleteData.failed.includes(row.boDDARefNo)) {
                    return <Badge bg="danger">Failed</Badge>;
                }
                if (!row.boDDARefNo || row.boDDARefNo === "") {
                    return <Badge bg="secondary">Just Signed Up</Badge>;
                }

                switch (row.requestType) {
                    case "Creation":
                        return <Badge bg="success">Active</Badge>;
                    case "Cancellation":
                        return <Badge bg="secondary">Cancel Triggered</Badge>;
                    default:
                        return <Badge bg="primary">Not Started</Badge>;
                }
            },
        },
        {
            name: "DDA Ref",
            selector: (row) => row.boDDARefNo || "-",
            sortable: true,
            width: "150px"
        },
        {
            name: "Staff",
            selector: (row) => row.staff_name,
            sortable: true,
            width: "150px"
        },
        {
            name: "Timestamp",
            selector: (row) => formatDateTime(row.timestamp),
            sortable: true,
            width: "300px",
            cell: (row) => (
                <div className="d-flex align-items-center gap-1">
                    <FiCalendar size={14} className="text-muted" />
                    <span>{formatDateTime(row.timestamp)}</span>
                </div>
            )
        },
        {
            name: "Action",
            width: "180px",
            cell: (row) => {
                const isMissing = incompleteData.missing.includes(row.boDDARefNo);
                const isFailed = incompleteData.failed.includes(row.boDDARefNo);
                const isJustSignedUp = !row.boDDARefNo || row.boDDARefNo === "";
                const isGuarantor = row.guarantor_for && row.guarantor_for !== "";

                if (isJustSignedUp) {
                    return (
                        <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleStartEgiro(row.customer_id)}
                            className="d-flex align-items-center gap-1"
                        >
                            <FiPlay size={14} />
                            Start eGIRO
                        </Button>
                    );
                }

                if (isMissing || isFailed) {
                    return (
                        <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => handleResetCustomer(row)}
                            className="d-flex align-items-center gap-1"
                        >
                            <FiRotateCcw size={14} />
                            Reset to Signup
                        </Button>
                    );
                }

                if (row.requestType === "Cancellation") {
                    return (
                        <Badge bg="secondary" className="px-3 py-2">
                            Cancelled
                        </Badge>
                    );
                }

                if (isGuarantor) {
                    return (
                        <Badge bg="info" className="px-3 py-2">
                            Guarantor
                        </Badge>
                    );
                }

                return (
                    <div className="d-flex align-items-center gap-3">
                        {row.cancelFlag === 1 ? (
                            <Badge bg="success">Enabled</Badge>
                        ) : (
                            <Badge bg="danger">Disabled</Badge>
                        )}

                        <span
                            role="button"
                            onClick={() =>
                                handleUpdateCustomer(row.customer_id, {
                                    cancelFlag: row.cancelFlag === 1 ? 0 : 1,
                                })
                            }
                            style={{ cursor: "pointer", fontSize: "1.5rem" }}
                        >
                            {row.cancelFlag === 1 ? (
                                <FiToggleRight className="text-success" />
                            ) : (
                                <FiToggleLeft className="text-danger" />
                            )}
                        </span>
                    </div>
                );
            },
        },
    ];

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "60vh" }}>
                <Spinner animation="border" variant="secondary" />
                <span className="ms-2 text-muted">Loading access data...</span>
            </div>
        );
    }

    return (
        <div className="container-fluid py-2">
            <ToastContainer position="top-right" autoClose={1000} />

            <div className="row mb-4">
                <div className="col-12">
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3">
                        <div className="flex-grow-1">
                            <div className="d-flex flex-column flex-lg-row align-items-start align-items-lg-center gap-3 mb-2">
                                <h5 className="fw-bold mb-0" style={{ fontSize: "16px" }}>
                                    Access Control Dashboard
                                    <span className="ms-2 text-muted" style={{ fontSize: "0.9rem" }}>
                                        ({stats.totalCustomers} Total Customers)
                                    </span>
                                </h5>

                                {activeFilter && (
                                    <div className="d-flex align-items-center">
                                        <Badge bg="primary" className="px-2 py-1 d-flex align-items-center gap-1">
                                            <span className="text-nowrap">
                                                Filter: {activeFilter.type === 'active' ? 'Active Customers' :
                                                    activeFilter.type === 'cancelTriggered' ? 'Cancel Triggered' :
                                                        activeFilter.type === 'justSignedUp' ? 'Just Signed Up' :
                                                            activeFilter.type === 'missing' ? 'Missing Status' :
                                                                activeFilter.type === 'failed' ? 'Failed Creation' :
                                                                    activeFilter.type === 'incomplete' ? 'All Incomplete' :
                                                                        activeFilter.type === 'privilegeEnabled' ? 'Privilege Enabled' :
                                                                            activeFilter.type === 'guarantors' ? 'Guarantors' : 'All Customers'}
                                            </span>
                                            <Button
                                                variant="link"
                                                className="p-0 text-white lh-1 fs-6"
                                                onClick={clearFilters}
                                            >
                                                <FiX size={12} />
                                            </Button>
                                        </Badge>
                                    </div>
                                )}
                            </div>

                            <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-primary"
                                    style={{ backgroundColor: '#eff6ff' }}>
                                    <div className="rounded-circle" style={{ width: '6px', height: '6px', backgroundColor: '#1d4ed8' }} />
                                    <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#1e40af' }}>
                                        {stats.activeCustomersCount} Active
                                    </span>
                                </div>

                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-success"
                                    style={{ backgroundColor: '#dcfce7' }}>
                                    <div className="rounded-circle" style={{ width: '6px', height: '6px', backgroundColor: '#16a34a' }} />
                                    <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#166534' }}>
                                        {stats.guarantors} Guarantors
                                    </span>
                                </div>

                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-warning"
                                    style={{ backgroundColor: '#fef9c3' }}>
                                    <div className="rounded-circle" style={{ width: '6px', height: '6px', backgroundColor: '#d97706' }} />
                                    <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#854d0e' }}>
                                        {stats.justSignedUpCount} Just Signed Up
                                    </span>
                                </div>

                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-danger"
                                    style={{ backgroundColor: '#fee2e2' }}>
                                    <div className="rounded-circle" style={{ width: '6px', height: '6px', backgroundColor: '#dc2626' }} />
                                    <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#991b1b' }}>
                                        {stats.totalIncompleteCount} Incomplete
                                    </span>
                                </div>

                                <div className="d-flex align-items-center gap-1 px-2 py-1 rounded border border-success"
                                    style={{ backgroundColor: '#d1fae5' }}>
                                    <div className="rounded-circle" style={{ width: '6px', height: '6px', backgroundColor: '#10b981' }} />
                                    <span className="fw-semibold" style={{ fontSize: '0.75rem', color: '#065f46' }}>
                                        {stats.privilegeEnabled} Privilege Enabled
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="d-flex flex-wrap gap-2 justify-content-start justify-content-md-end">
                            <div className="d-flex gap-2">
                                <Button
                                    variant="outline-secondary"
                                    size="sm"
                                    className="d-flex align-items-center gap-2 border"
                                    style={{ fontSize: '0.75rem' }}
                                    onClick={fetchAllData}
                                    disabled={loading}
                                >
                                    <FiRefreshCw size={14} className={loading ? "spinning" : ""} />
                                    <span className="d-none d-sm-inline">Refresh</span>
                                </Button>

                                {activeFilter && (
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        className="d-flex align-items-center gap-2 border"
                                        style={{ fontSize: '0.75rem' }}
                                        onClick={clearFilters}
                                    >
                                        <span className="d-none d-sm-inline">Clear Filter</span>
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Row className="mb-4 g-3">
                <Col md={3}>
                    <StatCard
                        title="Active Customers"
                        value={stats.activeCustomersCount}
                        subtitle="Successfully created eGIRO"
                        icon={<FiCheckCircle />}
                        color="#16a34a"
                        filterType="active"
                        isActive={activeFilter?.type === 'active'}
                        onClick={() => handleStatCardClick('active')}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="Guarantors"
                        value={stats.guarantors}
                        subtitle="Acting as guarantor"
                        icon={<FiShield />}
                        color="#3b82f6"
                        filterType="guarantors"
                        isActive={activeFilter?.type === 'guarantors'}
                        onClick={() => handleStatCardClick('guarantors')}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="Just Signed Up"
                        value={stats.justSignedUpCount}
                        subtitle="eGIRO process not started"
                        icon={<FiUser />}
                        color="#8b5cf6"
                        filterType="justSignedUp"
                        isActive={activeFilter?.type === 'justSignedUp'}
                        onClick={() => handleStatCardClick('justSignedUp')}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="Privilege Enabled"
                        value={stats.privilegeEnabled}
                        subtitle="Can cancel eGIRO"
                        icon={<FiUserCheck />}
                        color="#10b981"
                        filterType="privilegeEnabled"
                        isActive={activeFilter?.type === 'privilegeEnabled'}
                        onClick={() => handleStatCardClick('privilegeEnabled')}
                    />
                </Col>
            </Row>

            <Row className="mb-4 g-3">
                <Col md={3}>
                    <StatCard
                        title="Incomplete (All)"
                        value={stats.totalIncompleteCount}
                        subtitle="Missing + Failed"
                        icon={<FiAlertTriangle />}
                        color="#dc2626"
                        filterType="incomplete"
                        isActive={activeFilter?.type === 'incomplete'}
                        onClick={() => handleStatCardClick('incomplete')}
                    />
                </Col>
                <Col md={3}>
                    <StatCard
                        title="Missing Status"
                        value={stats.missingCount}
                        subtitle="Bank server down during signup"
                        icon={<FiClock />}
                        color="#f59e0b"
                        filterType="missing"
                        isActive={activeFilter?.type === 'missing'}
                        onClick={() => handleStatCardClick('missing')}
                    />
                </Col><Col md={3}>
                    <StatCard
                        title="Failed Creation"
                        value={stats.failedCount}
                        subtitle="No consent → eDDA failed"
                        icon={<FiXCircle />}
                        color="#ef4444"
                        filterType="failed"
                        isActive={activeFilter?.type === 'failed'}
                        onClick={() => handleStatCardClick('failed')}
                    />
                </Col>
            </Row>

            <Card className="border-0 shadow-sm">
                <Card.Body className="p-0">
                    <div className="p-3 border-bottom bg-gray-50">
                        <div className="row g-2 align-items-center">
                            <div className="col-md-6">
                                <div className="input-group" style={{ maxWidth: "400px" }}>
                                    <span className="input-group-text bg-white border-end-0">
                                        <FiSearch size={16} className="text-gray-500" />
                                    </span>
                                    <input
                                        type="text"
                                        className="form-control border-start-0"
                                        placeholder="Search customers by name, email, phone..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        style={{ fontSize: "0.875rem" }}
                                    />
                                </div>
                            </div>
                            <div className="col-md-6">
                                <div className="d-flex justify-content-end gap-2">
                                    <Badge bg="primary" className="px-2 py-1">
                                        {displayData.length} Records
                                    </Badge>
                                    {activeFilter && (
                                        <Badge bg="info" className="px-2 py-1">
                                            {activeFilter.type === 'active' ? 'Active Customers' :
                                                activeFilter.type === 'cancelTriggered' ? 'Cancel Triggered' :
                                                    activeFilter.type === 'justSignedUp' ? 'Just Signed Up' :
                                                        activeFilter.type === 'missing' ? 'Missing Status' :
                                                            activeFilter.type === 'failed' ? 'Failed Creation' :
                                                                activeFilter.type === 'incomplete' ? 'All Incomplete' :
                                                                    activeFilter.type === 'privilegeEnabled' ? 'Privilege Enabled' :
                                                                        activeFilter.type === 'guarantors' ? 'Guarantors' : 'All Customers'}
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-3">
                        <DataTable
                            columns={columns}
                            data={displayData}
                            pagination
                            paginationPerPage={10}
                            paginationRowsPerPageOptions={[5, 10, 15, 20]}
                            highlightOnHover
                            responsive
                            striped
                            persistTableHead
                            fixedHeader
                            fixedHeaderScrollHeight="calc(100vh - 400px)"
                            customStyles={{
                                headCells: {
                                    style: {
                                        backgroundColor: "#f9fafb",
                                        fontWeight: "600",
                                        fontSize: "0.75rem",
                                        textTransform: "uppercase",
                                        letterSpacing: "0.5px",
                                        color: "#374151",
                                        borderBottom: "1px solid #e5e7eb"
                                    },
                                },
                                cells: {
                                    style: {
                                        fontSize: "0.875rem",
                                        paddingTop: "12px",
                                        paddingBottom: "12px",
                                        color: "#374151"
                                    }
                                }
                            }}
                            noDataComponent={
                                <div className="text-center py-5">
                                    <h6>No data available</h6>
                                    <p className="text-muted">
                                        {activeFilter
                                            ? `No customers found for this filter`
                                            : "No customers found"}
                                    </p>
                                </div>
                            }
                        />
                    </div>
                </Card.Body>
            </Card>
        </div>
    );
};

export default Access;